import PullToRefresh from "@dockethealth/pulltorefreshjs";
import { vibrateDevice } from "./capacitor";

export function computePiecewiseLinear(
  x: number,
  segments: { startingAtX: number; denominator: number }[]
): number {
  let ret = 0;

  for (let i = 0; i < segments.length; i++) {
    const { startingAtX, denominator } = segments[i];
    const nextStartingAtX = segments[i + 1]?.startingAtX ?? Infinity;

    if (x <= startingAtX) {
      break;
    }
    const segmentEnd = Math.min(x, nextStartingAtX);
    const segmentContribution = (segmentEnd - startingAtX) / denominator;

    ret += segmentContribution;
  }

  return ret;
}

export function initPullToRefresh(params: {
  pullToRefreshMainElement: HTMLElement;
  onRefresh: PullToRefresh.Options["onRefresh"];
  shouldPullToRefresh: PullToRefresh.Options["shouldPullToRefresh"];
  onDistance?: PullToRefresh.Options["onDistance"];
}) {
  return PullToRefresh.init({
    mainElement: `#${params.pullToRefreshMainElement.id}`,
    triggerElement: "body",
    /**
     * Setting this >0 causes a delay before the refresh function is triggered. This can be useful to make it _feel_ to
     * the user like something significant is happening when we typically are just firing off a promise without awaiting
     * it to conduct the refresh.
     *
     * Note: Our forked version of the library no longer removes the PTR element from the DOM, so configuring this to
     * match expected animation times is not necessary.
     */
    refreshTimeout: 300,
    distThreshold: 80,
    distReload: 80,
    onReleasingWhilePulling: () => {
      vibrateDevice();
    },
    /**
     * Could probably use a hill function here instead, but using a piecewise linear function over such a small distance
     * feels to the user like a fairly smooth increase in resistance.
     * https://www.desmos.com/calculator/ey3mljtzxg
     *
     * This also a very rough approximation of the iOS bouncing effect, which helps keep the distance resisted roughly
     * feeling in line with the user's scrolling when overscrolling the PTR contents.
     */
    distanceResistedFunction: ({ distExtra }) => {
      const segments = [
        { startingAtX: 0, denominator: 1 },
        { startingAtX: 10, denominator: 1.25 },
        { startingAtX: 25, denominator: 1.5 },
        { startingAtX: 50, denominator: 2 },
        { startingAtX: 75, denominator: 3 },
        { startingAtX: 120, denominator: 5 },
      ];
      const ret = computePiecewiseLinear(distExtra, segments);
      return ret;
    },
    // Provide a fake css prop. We use `onDistance` to fully control the PTR element instead.
    cssProp: "_xxx_",
    // Override styles to an empty string. We set these in `app.css`
    getStyles: () => {
      return "";
    },
    onDistance: params.onDistance,
    getMarkup: () => {
      return `
<div class="__PREFIX__box">
    <img
        class="w-8"
        src="/assets/images/Icon_Immunizations_White.png"
        alt={"Immunizations"}
    />
</div>
`;
    },
    shouldPullToRefresh: params.shouldPullToRefresh,
    onRefresh: params.onRefresh,
  });
}
